import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material"
import React, { useEffect } from "react"
import { theme } from "themes/default/theme"
import HideImageIcon from "@mui/icons-material/HideImage"
import ImageIcon from "@mui/icons-material/Image"
import PropTypes from "prop-types"
import { maxWidth600 } from "themes/default/breakpoints"
import Image from "components/Image"
import useApi from "modules/account"
import { useAuthContext } from "contexts/AuthContext"
import { styles } from "./TextToolBar.styles"
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill"
import useSound from "hooks/useSound"
import { soackedManCorrect } from "utils/sounds"
import { fontImagesBW, fontsImages } from "utils/images"
import { useThemeContext } from "contexts/ThemeContext"
import {
  fontOptions,
  bgOptions,
  fontFamilyOptions,
} from "common/textEditorConstants"

const iconStyles = {
  color: theme.palette.grey[500],
}

TextToolBar.propTypes = {
  updateFontSize: PropTypes.func,
  updateBgColor: PropTypes.func,
  updateFontStyle: PropTypes.func,
  initialFontStyle: PropTypes.string,
  updateImageOption: PropTypes.func,
  showImageOption: PropTypes.bool,
  sx: PropTypes.object,
  hiddenBgIndexes: PropTypes.arrayOf(PropTypes.number),
  hiddenFontIndexes: PropTypes.arrayOf(PropTypes.number),
  fontStyleSelect: PropTypes.bool,
  showMarkText: PropTypes.bool,
  onMarkTextClick: PropTypes.func,
  showFollowLectureButton: PropTypes.bool,
}

export default function TextToolBar({
  updateFontSize,
  updateBgColor,
  updateFontStyle,
  initialFontStyle = "Poppins Regular",
  updateImageOption,
  showImageOption = false,
  sx = {},
  hiddenBgIndexes = [],
  hiddenFontIndexes = [],
  hiddenFontStyles = [],
  showMarkText = false,
  onMarkTextClick,
  showFollowLectureButton = false,
}) {
  const { textEditorSettings, setTextEditorSettings } = useAuthContext()
  const { setFontFamily, setFontSize, setBgColor, mode } = useThemeContext()
  const [showImage, setShowImage] = React.useState(true)
  const [fontStyle, setFontStyle] = React.useState(initialFontStyle)
  const [followLectureOn, setFollowLectureOn] = React.useState(true)
  const { updateUserTextSettings } = useApi()
  const [playSelect] = useSound(soackedManCorrect, { volume: 0.5 })
  const myTheme = useTheme()

  useEffect(() => {
    if (textEditorSettings?.fontSize >= 0) {
      const selectedFontSize = fontOptions[textEditorSettings?.fontSize]
      if (
        selectedFontSize &&
        typeof updateFontSize === "function" &&
        hiddenFontIndexes?.includes(textEditorSettings?.fontSize) === false
      ) {
        updateFontSize(selectedFontSize?.fontSize)
        setFontSize(selectedFontSize?.fontSize)
        setShowImage(selectedFontSize?.showImage)
      }
    }
    if (
      textEditorSettings?.backgroundColor >= 0 &&
      hiddenBgIndexes?.includes(textEditorSettings?.backgroundColor) === false
    ) {
      const selectedBgColor = bgOptions[textEditorSettings?.backgroundColor]
      if (selectedBgColor && typeof updateBgColor === "function") {
        if (myTheme.palette.mode === "dark") {
          updateBgColor("#1d2329")
        } else {
          updateBgColor(selectedBgColor?.bg)
        }
      }
    }
    if (
      textEditorSettings?.fontFamily >= 0 &&
      typeof updateFontStyle === "function" &&
      hiddenFontStyles?.includes(textEditorSettings?.fontFamily) === false
    ) {
      const selectedFontStyle =
        fontFamilyOptions[textEditorSettings?.fontFamily]
      if (selectedFontStyle) {
        setFontStyle(selectedFontStyle?.name)
        updateFontStyle(selectedFontStyle?.name)
        setFontFamily(selectedFontStyle?.name)
      }
    }
    if (textEditorSettings?.ruleEnabled !== undefined) {
      setFollowLectureOn(textEditorSettings?.ruleEnabled)
    }
  }, [])

  useEffect(() => {
    if (typeof updateBgColor !== "function") {
      return
    }
    if (myTheme.palette.mode === "dark") {
      updateBgColor("#1d2329")
    } else {
      // updateBgColor(themeBgColor)
    }
  }, [mode])

  useEffect(() => {
    if (updateImageOption) {
      updateImageOption(showImage)
    }
  }, [showImage])

  const onToolFontStyleClick = (font, index) => {
    playSelect()
    // const font =
    //   fontStyle !== "OpenDyslexic Regular"
    //     ? "OpenDyslexic Regular"
    //     : "Poppins Regular"
    setFontStyle(font)
    updateFontStyle(font)
    setFontFamily(font)
    const data = {
      ...textEditorSettings,
      fontFamily: index,
    }
    updateUserTextSettings(data).then((res) => {
      setTextEditorSettings(data)
    })
  }

  const onToolFontClick = (e) => {
    playSelect()
    if (e.fontSize !== undefined) {
      updateFontSize(e.fontSize)
      setFontSize(e.fontSize)
      const data = {
        ...textEditorSettings,
        fontSize: fontOptions.findIndex((item) => item.fontSize === e.fontSize),
      }
      updateUserTextSettings(data).then((res) => {
        setTextEditorSettings(data)
      })
    }
    if (e.showImage !== undefined && updateImageOption) {
      updateImageOption(e.showImage)
    }
  }

  const onToolBgClick = (e) => {
    playSelect()
    if (e.bg !== undefined) {
      if (myTheme.palette.mode === "dark") {
        updateBgColor("#1d2329")
      } else {
        updateBgColor(e.bg)
      }
      setBgColor(e.bg)
      const data = {
        ...textEditorSettings,
        backgroundColor: bgOptions.findIndex((item) => item.bg === e.bg),
      }
      updateUserTextSettings(data).then((res) => {
        setTextEditorSettings(data)
      })
    }
  }

  const onLectureClick = () => {
    playSelect()
    const value = !followLectureOn
    const data = {
      ...textEditorSettings,
      ruleEnabled: value,
    }
    setFollowLectureOn(value)
    updateUserTextSettings(data).then((res) => {
      setTextEditorSettings(data)
    })
  }

  return (
    <Box
      display="flex"
      flexDirection="row"
      sx={{
        border: `1px solid ${myTheme.palette.border.primary}`,
        backgroundColor: myTheme.palette.background.default,
        p: 0.5,
        [maxWidth600]: {
          flexWrap: "wrap",
        },
        ...sx,
      }}
    >
      {showMarkText && (
        <Box sx={styles.optionBox} onClick={onMarkTextClick}>
          <FormatColorFillIcon />
        </Box>
      )}
      {typeof updateFontStyle === "function" && (
        <Stack direction={"row"} alignItems="center" flexWrap={"wrap"}>
          {fontFamilyOptions.map((font, index) => {
            if (hiddenFontStyles?.includes(index)) return null
            return (
              <Tooltip
                title={font.label}
                arrow
                disableFocusListener
                key={index}
              >
                <Box
                  sx={styles.optionBox}
                  onClick={() => onToolFontStyleClick(font.name, index)}
                >
                  <Image
                    key={`font-icon-${fontStyle}-${font.name}`}
                    src={
                      fontStyle === font.name
                        ? fontsImages[font.icon]
                        : fontImagesBW[font.icon]
                    }
                    width={32}
                    height={32}
                    alt={font.name}
                  />
                </Box>
              </Tooltip>
            )
          })}
        </Stack>
      )}
      {fontOptions.map((tool, index) =>
        hiddenFontIndexes?.includes(index) ? null : (
          <Box
            key={index}
            sx={[
              styles.optionBox,
              textEditorSettings?.fontSize === index &&
                styles.optionBoxSelected,
            ]}
            onClick={() => onToolFontClick(tool)}
          >
            <Typography
              variant="body3"
              sx={{
                fontSize: tool.fontSize || 14,
                color:
                  textEditorSettings?.fontSize === index ? "white" : tool.color,
              }}
            >
              {tool.name}
            </Typography>
          </Box>
        )
      )}
      {bgOptions.map((tool, index) =>
        hiddenBgIndexes?.includes(index) ? null : (
          <Box
            key={index}
            sx={[
              styles.optionBox,
              textEditorSettings?.backgroundColor === index &&
                styles.optionBoxSelected,
              { backgroundColor: tool.bg },
            ]}
            onClick={() => onToolBgClick(tool)}
          >
            <Typography
              variant="body3"
              sx={{
                fontSize: tool.fontSize || 14,
                color: tool.color,
              }}
            >
              {tool.name}
            </Typography>
          </Box>
        )
      )}
      {showImageOption && (
        <Box sx={styles.optionBox} onClick={() => setShowImage(!showImage)}>
          {!showImage ? (
            <ImageIcon sx={iconStyles} />
          ) : (
            <HideImageIcon sx={iconStyles} />
          )}
        </Box>
      )}
      {showFollowLectureButton && (
        <Stack
          direction={"row"}
          flex={1}
          justifyContent="flex-end"
          alignItems="center"
        >
          <Box
            sx={[styles.optionBox, styles.lastOptionBox]}
            onClick={onLectureClick}
          >
            {followLectureOn && (
              <Image name={"lecture-on"} width={32} height={32} />
            )}
            {!followLectureOn && (
              <Image name={"lecture-off"} width={32} height={32} />
            )}
          </Box>
        </Stack>
      )}
    </Box>
  )
}
